import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import './Home.css';

const Home = () => {
  const [stats, setStats] = useState({ users: 100, polls: 500, votes: 1000 });
  const [topics, setTopics] = useState([]);
  const [error, setError] = useState(null);
  const [cffpSeason, setCffpSeason] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
  const { ref: statsRef, inView: statsInView } = useInView({ triggerOnce: false });

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const topicsRes = await axios.get(`${apiUrl}/api/topics/showcase`);
        setTopics(topicsRes.data); // No need for slicing setTopics(topicsRes.data.slice(0, 4));
      } catch (error) {
        //console.error('Error fetching topics:', error.response?.data || error.message);
        setError('Failed to load featured polls. Please try again later.');
      }
    };

    const fetchStats = async () => {
      try {
        const statsRes = await axios.get(`${apiUrl}/api/topics/stats`);
        //console.log('Fetched stats:', statsRes.data); // Debug log
        setStats({
          users: statsRes.data.userCount,
          polls: statsRes.data.pollCount,
          votes: statsRes.data.voteCount
        });
        setError(null);
      } catch (error) {
        //console.error('Error fetching stats:', error.response?.data || error.message);
        setError('Failed to load stats. Please try again later.');
      }
    };

    const fetchFantasy = async () => {
      try {
        const fantasyRes = await axios.get(`${apiUrl}/api/fantasy/hub`);
        setCffpSeason(fantasyRes.data?.sports?.cfb || null);
      } catch (err) {
        setCffpSeason(null);
      }
    };

    fetchTopics();
    fetchFantasy();
    if (statsInView) {
      fetchStats();
    }
  }, [apiUrl, statsInView]);

  return (
    <div className="home-container">
      {/* Masthead */}
      <section className="masthead">
        <p className="masthead-kicker">Crowd rankings, your way</p>
        <h1>Welcome to MyTop25</h1>
        <h2>#1 Crowd Voting Platform</h2>
        <Link to="/system-topics" className="view-button">
          Start Voting — It’s Free
        </Link>
      </section>

      <section className="cffp-banner">
        <div className="cffp-banner-copy">
          <p className="cffp-kicker">New on MyTop25</p>
          <h2>College Football Fantasy Playoff</h2>
          <p>
            {cffpSeason && cffpSeason.status !== 'draft'
              ? `${cffpSeason.year} is live. Pick your school, tap four real games into your playoff, and climb the leaderboard.`
              : 'Pick one team, choose four regular-season games as your playoff, and battle everyone else on record and strength of schedule.'}
          </p>
        </div>
        <Link to="/college-fantasy" className="view-button cffp-banner-button">
          Enter CFFP
        </Link>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="how-it-works-item">
          <h4>1. Sign On</h4>
          <p>Use your email for a safe and secure magic link, no password needed</p>
        </div>
        <div className="how-it-works-item">
          <h4>2. Crowd Vote</h4>
          <p>Start Voting on Official Polls</p>
        </div>
        <div className="how-it-works-item">
          <h4>3. Create Your Own Polls</h4>
          <p>Make your own polls to share with friends</p>
        </div>
      </section>

      {/* Site Statistics */}
      <section className="stats-banner" ref={statsRef}>
        {error && <p className="error-message">{error}</p>}
        <div className="stats-item">
          <span>Participants: </span>
          <span>
            <CountUp end={stats.users} duration={2} separator="," />
          </span>
        </div>
        <div className="stats-item">
          <span>Polls: </span>
          <span>
            <CountUp end={stats.polls} duration={2} separator="," />
          </span>
        </div>
        <div className="stats-item">
          <span>Votes: </span>
          <span>
            <CountUp end={stats.votes} duration={2} separator="," />
          </span>
        </div>
      </section>

      {/* System Topics */}
      <section className="system-topics">
        <h2>Featured Official Polls</h2>
        <div className="topics-grid">
          {topics.length === 0 && !error && <p>Loading polls...</p>}
          {topics.map(topic => (
            <div
              key={topic._id}
              className={`topic-mini-card ${topic.genre || 'default'}`}
              style={{ backgroundImage: `url(/bg-${topic.genre || 'default'}.png)` }}
            >
              <h3>{topic.name}</h3>
              <Link to={`/topic/${topic._id}`} className="view-button">View</Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
